import React, {
    ChangeEvent,
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
} from "react";
import { MaskTypes } from "../../../interfaces";
import { useGlobal } from "../../../hooks/global";
import { ContainerField } from "../../../style";
import { ColorInput } from "@mantine/core";

export interface InputRefProps {
    getValue: () => string;
    setValue: (newValue: string) => void;
    focus: () => void;
}

interface InputProps
    extends React.DetailedHTMLProps<
        React.InputHTMLAttributes<HTMLInputElement>,
        HTMLInputElement
    > {
    mask?: MaskTypes;
    noUpperCase?: boolean;
    isPassword?: boolean;
    error?: any;
    options?: any;
    getIsDisabled?: (form: any) => boolean;
}

const InputColor: React.ForwardRefRenderFunction<InputRefProps, InputProps> = (
    props,
    ref
) => {
    const {
        name,
        disabled = false,
        options = { onChange: () => { } },
        error,
        onChange = () => { },
        getIsDisabled,
        ...rest
    } = props;

    const { theme } = useGlobal();
    const inputRef = useRef<HTMLInputElement>(null);

    const handleOnChange = useCallback((color: string) => {
        onChange(color as unknown as ChangeEvent<HTMLInputElement>);
        options.onChange(color);
    }, [onChange]);

    const getValue = () => inputRef.current?.value || "";

    const setValue = useCallback(
        (newValue: string) => {
            if (inputRef.current) {
                inputRef.current.value = newValue;
            }
        },
        []
    );

    const focus = useCallback(() => inputRef.current?.focus(), []);

    useImperativeHandle(ref, () => ({
        getValue,
        setValue,
        focus,
    }));

    const isDisabled = getIsDisabled ? getIsDisabled('form') : false;
    return (
        <ContainerField style={{ flex: 1 }}>
            <ColorInput
                withEyeDropper={false}
                name={name}
                id={name}
                ref={inputRef}
                disabled={isDisabled || disabled}
                styles={{
                    input: {
                        backgroundColor: theme === 'dark' ? '#242322' : '#fff',
                        border: theme === 'dark' ? '1px solid rgb(68 68 68)' : '1px solid #CCCCCC',
                        color: theme === 'dark' ? '#8a8a8a' : '#3a3a3a',
                        padding: 18
                    },
                }}
                error={error}
                onChange={handleOnChange}
                {...rest}
                {...options}
            />
        </ContainerField>
    );
};

export default forwardRef(InputColor);
